<template>
  <div>
    <!-- Elektrischer Anschlusswert & Inbetriebnahmedatum -->
    <RowItem
      first
      class="row-item consumption-device-title"
      :title="`${$t(
        `consumption_devices.${currentDeviceName}`
      )} <br>Gerät ID: ${shortDeviceUuid}`"
      :editable="false"
    />

    <!-- Elektrischer Anschlusswert -->
    <RowItem
      class="row-item"
      field="TODO: add correct field"
      :title="
        $t('consumption_devices.order_details.electrical_connection_value')
      "
      :editable="true"
      @edit="
        $emit('edit-component', {
          component: 'ConsumptionDeviceValueAndCheckbox',
          location: 'technicalDetailsComponent',
          properties: deviceValueAndCheckboxProps,
        })
      "
    >
      <div v-html="presentablePerformanceW"></div>
    </RowItem>

    <!-- Inbetriebnahmedatum und Installationsunternehmen -->
    <RowItem
      class="row-item"
      :title="presentalbeDateAndInstallerTitles"
      :editable="true"
      @edit="
        $emit('edit-component', {
          component: 'InstallationCompanyAndDateComponent',
          location: 'technicalDetailsComponent',
          properties: deviceCommissioningProps,
        })
      "
    >
      <div v-html="presentableDateAndInstallerObject"></div>
    </RowItem>

    <!-- Zählernummer -->
    <RowItem
      class="row-item"
      :title="$t('consumption_devices.order_details.counter_number')"
      :editable="true"
      :last="
        currentDeviceName !== 'wallbox' &&
        (!showInstallerJourneyFields || !isDeviceReadyForInspection)
      "
      @edit="
        $emit('edit-component', {
          component: 'ConsumptionDeviceCounter',
          location: 'technicalDetailsComponent',
          properties: deviceCounterProps,
        })
      "
    >
      {{ consumptionDevice.counter_id }}
    </RowItem>

    <RowItem
      class="row-item"
      :title="$t('consumption_devices.order_details.billing_module')"
      :editable="!!deviceCounterProps.billingModule"
      @edit="
        $emit('edit-component', {
          component: 'BillingModuleComponent',
          location: 'technicalDetailsComponent',
          properties: deviceCounterProps.billingModule,
        })
      "
    >
      <div>{{ presentableBillingModule }}</div>
    </RowItem>

    <!-- wallbox cooridnates -->
    <RowItem
      v-if="
        currentDeviceName === 'wallbox' && consumptionDevice.wallbox_coordinates
      "
      class="row-item"
      :title="$t('wallbox_coordinates.title')"
      :editable="true"
      :last="!showInstallerJourneyFields || !isDeviceReadyForInspection"
      @edit="
        $emit('edit-component', {
          component: 'WallboxCoordinatesComponent',
          location: 'technicalDetailsComponent',
          properties: {
            deviceName: currentDeviceName,
            deviceUuid,
            coordinates: consumptionDevice.wallbox_coordinates,
          },
        })
      "
    >
      {{ wallboxCoordinatesText }}
    </RowItem>

    <!-- Common technical data -->
    <CommonTechnicalData
      v-if="showInstallerJourneyFields && isDeviceReadyForInspection"
      :technicalData="consumptionDevice"
      :deviceName="currentDeviceName"
      @edit="
        $emit('edit-component', {
          component: 'CommonDeviceDataComponent',
          location: 'technicalDetailsComponent',
          properties: commonTechnicalProps,
        })
      "
    />

    <!-- Wallbox special data -->
    <WallboxSpecialData
      v-if="
        currentDeviceName === 'wallbox' &&
        showInstallerJourneyFields &&
        isDeviceReadyForInspection
      "
      :wallboxData="consumptionDevice"
      :deviceUuid="deviceUuid"
      :deviceName="currentDeviceName"
      @edit-component="$emit('edit-component', $event)"
    />

    <!-- Battery Storage special data -->
    <BatteryStorageSpecialData
      v-if="
        currentDeviceName === 'battery_storage' &&
        showInstallerJourneyFields &&
        isDeviceReadyForInspection
      "
      :batteryStorageData="consumptionDevice"
      :deviceUuid="deviceUuid"
      :deviceName="currentDeviceName"
      @edit-component="$emit('edit-component', $event)"
    />
  </div>
</template>

<script>
import { germanNumberFormat } from '@/helpers/globalFunctions';
import InstallerCompaniesService from '../../services/InstallerCompaniesService';
import RowItem from '../feed-in-system-details/RowItem';
import BatteryStorageSpecialData from './BatteryStorageSpecialData';
import CommonTechnicalData from './CommonTechnicalData.vue';
import WallboxSpecialData from './WallboxSpecialData.vue';

export default {
  name: 'SingleConsumptionDeviceDetails',
  props: {
    consumptionDevice: {
      type: Object,
      required: true,
    },
    currentDeviceName: {
      type: String,
      required: true,
    },
    deviceUuid: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      fetchedInstallerCompanies: [],
    };
  },
  components: {
    RowItem,
    BatteryStorageSpecialData,
    CommonTechnicalData,
    WallboxSpecialData,
  },
  async mounted() {
    await this.fetchInstallerCompanies();
  },
  computed: {
    selectedConsumptionDevice() {
      return this.$store.getters.getSelectedConsumptionDevice;
    },
    showInstallerJourneyFields() {
      return (
        this.selectedConsumptionDevice.status !== 'ORDER_CREATED' &&
        this.selectedConsumptionDevice.status !== 'CRITICAL_ORDER_CREATED'
      );
    },
    presentalbeDateAndInstallerTitles() {
      return `
        ${this.$t('consumption_devices.order_details.commissioning_date')}
        <br> ${this.$t('consumption_devices.order_details.installer_company')}`;
    },
    presentablePerformanceW() {
      return `${this.numberToGermanFormat(
        this.divideByThousand(this.consumptionDevice.performance_W)
      )} kW`;
    },
    presentableDateAndInstallerObject() {
      let installerCompany =
        this.consumptionDevice.installation_company_electricity;
      if (installerCompany === 'not_listed') {
        installerCompany = this.$t(
          'consumption_devices.order_details.not_listed'
        );
      }
      return `${this.formatDate(
        this.consumptionDevice.commissioning_date
      )}<br> ${installerCompany}`;
    },
    shortDeviceUuid() {
      return this.deviceUuid?.slice(0, 8) ?? '';
    },
    deviceValueAndCheckboxProps() {
      return {
        deviceName: this.currentDeviceName,
        deviceUuid: this.deviceUuid,
        cardLabel: this.$t(`consumption_devices.${this.currentDeviceName}`),
        checkboxValue: this.currentDeviceName,
        kwValue: this.divideByThousand(this.consumptionDevice.performance_W),
        iconName: 'NewIconWallPlug',
      };
    },
    deviceCommissioningProps() {
      return {
        ...this.consumptionDevice,
        companyList: this.fetchedInstallerCompanies,
        deviceTitle: this.$t(`consumption_devices.${this.currentDeviceName}`),
        deviceName: this.currentDeviceName,
        deviceUuid: this.deviceUuid,
      };
    },
    deviceCounterProps() {
      return {
        deviceName: this.currentDeviceName,
        deviceUuid: this.deviceUuid,
        deviceTitle: this.$t(`consumption_devices.${this.currentDeviceName}`),
        counterNumberValue: this.consumptionDevice.counter_id,
        picture: this.consumptionDevice.picture,
        supplyInstallationNumber:
          this.consumptionDevice.supply_installation_number || '',
        orderStatus: this.selectedConsumptionDevice.status,
        billingModule: {
          currentValue: this.consumptionDevice.billing_module,
          fieldName: `$.controllable_devices.${this.currentDeviceName}.${this.deviceUuid}`,
          title: this.$t('consumption_devices.order_details.billing_module'),
        },
      };
    },
    commonTechnicalProps() {
      return {
        deviceName: this.currentDeviceName,
        deviceUuid: this.deviceUuid,
        initialDeviceData: {
          manufacturer: this.consumptionDevice.manufacturer,
          type: this.consumptionDevice.type,
          controlType: this.consumptionDevice.control_type,
          loadManagement: this.consumptionDevice.load_management,
          controlBoxConnection: this.consumptionDevice.control_box_connection,
        },
      };
    },
    isDeviceReadyForInspection() {
      return !!this.consumptionDevice.ready_for_inspection;
    },
    wallboxCoordinatesText() {
      const coords = this.consumptionDevice.wallbox_coordinates;
      if (!coords) return '';
      const lat =
        typeof coords.lat === 'number' ? coords.lat.toFixed(6) : coords.lat;
      const lng =
        typeof coords.lng === 'number' ? coords.lng.toFixed(6) : coords.lng;
      return `${lat}, ${lng}`;
    },
    presentableBillingModule() {
      const billingModule = this.consumptionDevice.billing_module;
      if (!billingModule) return '-';

      // Convert billing module to readable German format (same as BillingModuleComponent)
      switch (billingModule) {
        case 'reduced_flat':
          return 'Modul 1 (Pauschale Netzentgeltreduzierung)';
        case 'reduced_separate':
          return 'Modul 2 (Prozentuale Arbeitspreisreduzierung)';
        default:
          return billingModule;
      }
    },
  },
  methods: {
    numberToGermanFormat(value) {
      return germanNumberFormat(value);
    },
    divideByThousand(str) {
      if (!str) return;
      return parseInt(str) / 1000;
    },
    formatDate(str) {
      const dateObj = new Date(str);

      const day = String(dateObj.getUTCDate()).padStart(2, '0');
      const month = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
      const year = dateObj.getUTCFullYear();

      return `${day}.${month}.${year}`;
    },
    async fetchInstallerCompanies() {
      try {
        const params = {
          zip: '',
          product: 'electricity',
        };
        const companies = await InstallerCompaniesService.getInstallerCompanies(
          params
        );
        this.fetchedInstallerCompanies = companies;
      } catch (err) {
        this.$frontendLogger.error(err);
      }
    },
  },
};
</script>

<style scoped>
.row-item {
  margin-block: 1px;
}

.consumption-device-title {
  font-size: 22px;
  font-weight: bold;
}
</style>

<style>
.consumption-device-title #action {
  margin-left: 0 !important;
}
</style>
